import React, { createContext, useContext, useState, useCallback } from 'react';
import ConfirmModal from '../components/common/ConfirmModal';

const ConfirmContext = createContext(null);

/**
 * Provider component that wraps the app and provides confirmation functionality
 */
export const ConfirmProvider = ({ children }) => {
  const [confirmState, setConfirmState] = useState({
    isOpen: false,
    title: '',
    message: '',
    confirmText: 'Confirm',
    cancelText: 'Cancel',
    type: 'confirm',
    resolve: null
  });

  /**
   * Show a confirmation modal and return a promise
   * @param {Object} options - Configuration options
   * @param {string} options.title - Modal title
   * @param {string} options.message - Modal message
   * @param {string} options.confirmText - Confirm button text
   * @param {string} options.cancelText - Cancel button text
   * @param {string} options.type - Modal type: 'confirm' | 'warning' | 'danger' | 'info'
   * @returns {Promise<boolean>} - Resolves to true if confirmed, false if cancelled
   */
  const confirm = useCallback((options = {}) => {
    return new Promise((resolve) => {
      setConfirmState({
        isOpen: true,
        title: options.title || 'Confirm Action',
        message: options.message || 'Are you sure you want to proceed?',
        confirmText: options.confirmText || 'Confirm',
        cancelText: options.cancelText || 'Cancel',
        type: options.type || 'confirm',
        resolve
      });
    });
  }, []);

  const handleConfirm = useCallback(() => {
    confirmState.resolve?.(true);
    setConfirmState((prev) => ({ ...prev, isOpen: false }));
  }, [confirmState.resolve]);

  const handleCancel = useCallback(() => {
    confirmState.resolve?.(false);
    setConfirmState((prev) => ({ ...prev, isOpen: false }));
  }, [confirmState.resolve]);

  return (
    <ConfirmContext.Provider value={{ confirm }}>
      {children}
      <ConfirmModal
        isOpen={confirmState.isOpen}
        onConfirm={handleConfirm}
        onCancel={handleCancel}
        title={confirmState.title}
        message={confirmState.message}
        confirmText={confirmState.confirmText}
        cancelText={confirmState.cancelText}
        type={confirmState.type}
      />
    </ConfirmContext.Provider>
  );
};

/**
 * Hook to access the confirm function
 * @returns {{ confirm: (options: Object) => Promise<boolean> }}
 *
 * @example
 * const { confirm } = useConfirm();
 *
 * // Simple usage (replaces window.confirm)
 * const confirmed = await confirm({
 *   message: 'Are you sure you want to delete this?'
 * });
 *
 * // Full options
 * const confirmed = await confirm({
 *   title: 'Delete Card',
 *   message: 'This action cannot be undone.',
 *   confirmText: 'Delete',
 *   cancelText: 'Keep',
 *   type: 'danger'
 * });
 */
export const useConfirm = () => {
  const context = useContext(ConfirmContext);
  if (!context) {
    throw new Error('useConfirm must be used within a ConfirmProvider');
  }
  return context;
};

export default ConfirmProvider;
